import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError, money } from "@/lib/api";

const PRESETS = [25, 50, 100, 250, 1000];

export default function DonateDialog({ open, onOpenChange, item, projectCode, budgetItem, suggested }) {
  const [amount, setAmount] = useState(suggested || 50);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [display, setDisplay] = useState("");
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        category: item?.key || "general", amount: Number(amount), origin_url: window.location.origin,
        project_code: projectCode || null, budget_key: budgetItem?.key || null,
        donor_name: name, donor_email: email || null, display_name: display, anonymous: anon,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="donate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl text-[#03045E]">{item?.label || "Donate"}</DialogTitle>
          <DialogDescription>{budgetItem ? `Toward "${budgetItem.label}" on ${projectCode}.` : projectCode ? `Supporting ${projectCode}.` : item?.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button type="button" key={p} onClick={() => setAmount(p)} data-testid={`donate-preset-${p}`}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors duration-200 ${Number(amount) === p ? "bg-[#03045E] text-white border-[#03045E]" : "bg-white border-sky-200 text-[#03045E] hover:bg-sky-50"}`}>{money(p)}</button>
            ))}
          </div>
          <div>
            <Label htmlFor="amt">Amount (USD)</Label>
            <Input id="amt" type="number" min={5} step="1" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="donate-amount-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="donate-name-input" /></div>
            <div><Label>Email (receipt)</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="donate-email-input" /></div>
          </div>
          <div><Label>Recognition name (shown publicly)</Label><Input value={display} disabled={anon} placeholder="e.g. ABC Company" onChange={(e) => setDisplay(e.target.value)} data-testid="donate-display-input" /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={anon} onCheckedChange={(v) => setAnon(!!v)} data-testid="donate-anonymous-checkbox" /> Give anonymously</label>
          <button disabled={busy || Number(amount) < 5} data-testid="donate-submit-button" className="w-full rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-bold uppercase py-3 flex items-center justify-center gap-2 disabled:opacity-60 transition-colors duration-200">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Continue to secure checkout
          </button>
          <p className="text-[11px] text-slate-500 text-center">Payments processed by Stripe. Minimum $5.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
